// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SectionWrapper, { SectionWrapperStyles } from "@adl/App/Components/Common/SectionWrapper";
import ShowcaseCard from "@adl/App/Widgets/Showcase/ShowcaseCard";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import * as React from "react";
import { css } from "styled-components";

interface ShowCaseCardFields {
    fields: {
        imageUrl: string;
        title: string;
        description: string;
        buttonTitle?: string;
        buttonLink: LinkFieldValue;
        backgroundColor: string;
        contentPosition: "flex-start" | "flex-end";
    };
}

const enum fields {
    title = "title",
    cards = "cards",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.cards]: ShowCaseCardFields[];
    };
}

type Props = OwnProps;

interface SolutionsWidgetStyles {
    container?: GridContainerProps;
    cardGridItem?: GridItemProps;
    sectionWrapperStyles?: SectionWrapperStyles;
}

const solutionsWidgetStyles: SolutionsWidgetStyles = {
    container: {
        gap: 22,
    },
    cardGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            justify-content: center;
        `,
    },
};

const styles = solutionsWidgetStyles;

const ShowCaseWidget: React.FC<Props> = ({ fields: { title, cards } }) => {
    if (!cards || cards.length === 0) {
        return null;
    }

    const renderShowCaseCard = (card: ShowCaseCardFields, index?: number) => {
        let otherProps = {};
        if (index) {
            otherProps = {
                key: index.toString(),
            };
        }
        return <ShowcaseCard {...card.fields} {...otherProps} />;
    };

    return (
        <SectionWrapper
            title={title}
            extendedStyles={styles.sectionWrapperStyles}
            data-test-selector="Aquifer-SolutionsWidget"
        >
            <GridContainer {...styles.container}>
                {cards.map((card, index) => (
                    <React.Fragment key={index.toString()}>
                        <GridItem {...styles.cardGridItem}>{renderShowCaseCard(card)}</GridItem>
                    </React.Fragment>
                ))}
            </GridContainer>
        </SectionWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ShowCaseWidget,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "imageUrl",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },

                    {
                        name: "description",
                        displayName: "Description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: "contentPosition",
                        displayName: "Content Position",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "flex-start",
                        options: [
                            {
                                value: "flex-start",
                                displayName: "left",
                            },
                            {
                                value: "flex-end",
                                displayName: "right",
                            },
                        ],
                    },
                    {
                        name: "buttonLink",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { value: "", type: "Page" },
                        isRequired: true,
                        validate: (link: LinkFieldValue) => {
                            if (!link || !link.value) {
                                return "A value must be provided.";
                            }
                            return null;
                        },
                    },
                    {
                        name: "buttonTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "backgroundColor",
                        displayName: "Text Background Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "rgba(15, 17, 54, 0.75)",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
